/**
 * Tabs Component
 *
 * A simple, clean tabs component for organizing content into tabbed sections.
 *
 * @example
 * ```jsx
 * const [activeTab, setActiveTab] = useState('documents');
 *
 * <Tabs
 *   activeTab={activeTab}
 *   onChange={setActiveTab}
 *   tabs={[
 *     { key: 'documents', label: 'Documents', count: 5 },
 *     { key: 'assets', label: 'Assets', count: 3 },
 *     { key: 'history', label: 'History' },
 *   ]}
 * />
 *
 * {activeTab === 'documents' && <DocumentsContent />}
 * {activeTab === 'assets' && <AssetsContent />}
 * ```
 */

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const Tabs = ({
	tabs = [],
	activeTab,
	onChange,
	className = '',
	size = 'default', // 'default' | 'sm'
}) => {
	return (
		<div className={classNames('border-b border-gray-200', className)}>
			<nav className="-mb-px flex space-x-6" aria-label="Tabs">
				{tabs.map((tab) => {
					const isActive = activeTab === tab.key;
					return (
						<button
							key={tab.key}
							onClick={() => onChange(tab.key)}
							className={classNames(
								isActive
									? 'border-indigo-500 text-indigo-600'
									: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
								'whitespace-nowrap border-b-2 py-3 font-medium flex items-center gap-2 transition-colors',
								size === 'sm' ? 'text-xs px-1' : 'text-sm px-1',
							)}
						>
							{tab.label}
							{typeof tab.count === 'number' && (
								<span
									className={classNames(
										isActive
											? 'bg-indigo-100 text-indigo-600'
											: 'bg-gray-100 text-gray-600',
										'rounded-full px-2 py-0.5 text-xs font-medium',
									)}
								>
									{tab.count}
								</span>
							)}
						</button>
					);
				})}
			</nav>
		</div>
	);
};

export default Tabs;
